package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.api.AuthApi
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * @param loopDetected true when automatic re-login was abandoned because it kept bouncing
 *   (ADV-443). Say so plainly rather than presenting an ordinary sign-in page the user has
 *   already watched fail several times.
 *
 *   ADV-1155: this page can never mean "the auth backend is down". `authedFetch` bounces the
 *   guard on a 401 or on a token that is simply absent; a 5xx falls through untouched and never
 *   records a bounce, so an unreachable auth backend (which surfaces as 503) cannot trip this.
 *   **Do not reintroduce outage wording here** — the old copy blamed the identity provider and
 *   sent a real 2026-09-02 investigation at a service that was healthy and answering correctly.
 *
 *   Note the absent-token branch: a browser that will not persist `drive.token` (Safari ITP or
 *   private browsing, a partitioned iframe, storage cleared) bounces every load and eventually
 *   trips this page, where "your session most likely expired" is a plausible-sounding but wrong
 *   explanation. That predates ADV-1155 and is not fixed here. If it turns up in support
 *   traffic, the fix is to stop counting the absent-token branch, not to reword this again.
 */
@Composable
fun LoginGate(loopDetected: Boolean = false) {
    Div(attrs = {
        style {
            property("padding", "96px 24px")
            property("text-align", "center")
        }
    }) {
        H1(attrs = {
            style {
                property("font-size", "32px")
                property("font-weight", "400")
                property("margin", "0 0 8px 0")
            }
        }) { Text("📁 Advaita Data Hub") }
        P(attrs = {
            style {
                property("color", if (loopDetected) "#c5221f" else "#5f6368")
                property("margin", "0 0 32px 0")
            }
        }) {
            Text(
                if (loopDetected) {
                    "Sign-in didn't complete after several attempts — your session most likely " +
                        "expired. Use the button below to try again, and contact support if it " +
                        "keeps happening."
                } else {
                    "Sign in with your Advaita account to continue."
                },
            )
        }
        Button(attrs = {
            classes("btn", "primary")
            onClick { AuthApi.startLogin() }
        }) { Text(if (loopDetected) "Try again" else "Sign in") }
    }
}

/**
 * Shown while a 401 is sending the user to /auth/login. ADV-1155.
 *
 * A spinner would be enough if navigations always committed. They do not:
 * `installBeforeUnloadWarning` raises the native "Leave site?" prompt while an upload is
 * running, and clicking "Stay" cancels the navigation with the token already cleared. So this
 * carries an explicit way out rather than leaving the user watching "Loading…" forever.
 */
@Composable
fun SigningInGate() {
    Div(attrs = {
        style {
            property("padding", "96px 24px")
            property("text-align", "center")
        }
    }) {
        H1(attrs = {
            style {
                property("font-size", "32px")
                property("font-weight", "400")
                property("margin", "0 0 8px 0")
            }
        }) { Text("📁 Advaita Data Hub") }
        P(attrs = {
            style {
                property("color", "#5f6368")
                property("margin", "0 0 32px 0")
            }
        }) { Text("Signing you in… If this page doesn't move on, use the button below.") }
        Button(attrs = {
            classes("btn", "primary")
            onClick { AuthApi.startLogin() }
        }) { Text("Sign in") }
    }
}
